/**
 * SideMenu - Mobile navigation drawer using shadcn/ui components
 *
 * Built with:
 * - Sheet for the drawer container
 * - ScrollArea for scrollable content (requires explicit height)
 * - Collapsible for expandable sections
 * - Button, Avatar, Separator from shadcn/ui
 *
 * Layout:
 * - Fixed header (user profile) - always visible
 * - Scrollable area (nav + footer) - ScrollArea with h-full + min-h-0 parent
 */

import { useState, useMemo, useCallback } from 'react';
import CONFIG from '../../config';
import { ColorModeSelector } from '../ColorModeSelector';
import { DemoModeToggle } from '../DemoModeToggle';
import type { ConversationSummary } from '../../types';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SquarePen,
  Clock,
  MessageSquare,
  Settings,
  ChevronRight,
  BookOpen,
  LogOut,
  ExternalLink,
  BarChart3,
  Users,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

interface SideMenuProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  onSignOut: () => void;
  onNewChat?: () => void;
  recentConversations?: ConversationSummary[];
  onSelectConversation?: (chatId: string) => void;
  currentChatId?: string;
}

export const SideMenu = ({
  isOpen,
  onClose,
  userEmail,
  onSignOut,
  onNewChat,
  recentConversations = [],
  onSelectConversation,
  currentChatId,
}: SideMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSettings, setShowSettings] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const isDashboard = location.pathname === '/dashboard';
  const isManagement = location.pathname === '/management';

  // Memoize user info extraction
  const { formattedName, formattedCompany } = useMemo(() => {
    const userName = userEmail?.split('@')[0] || 'User';
    const name = userName
      .split('.')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    const companyName = userEmail?.split('@')[1]?.split('.')[0] || 'Company';
    const company = companyName.charAt(0).toUpperCase() + companyName.slice(1);

    return { formattedName: name, formattedCompany: company };
  }, [userEmail]);

  // Memoize handlers
  const handleNewChat = useCallback(() => {
    onClose();
    onNewChat?.();
  }, [onClose, onNewChat]);

  const handleConversationSelect = useCallback(
    (chatId: string) => {
      onClose();
      onSelectConversation?.(chatId);
    },
    [onClose, onSelectConversation],
  );

  const handleSignOut = useCallback(() => {
    onClose();
    onSignOut();
  }, [onClose, onSignOut]);

  const handleDashboard = useCallback(() => {
    onClose();
    navigate('/dashboard');
  }, [onClose, navigate]);

  const handleManagement = useCallback(() => {
    onClose();
    navigate('/management');
  }, [onClose, navigate]);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="left"
        className="w-5/6 max-w-sm p-0 flex flex-col bg-background"
      >
        {/* Accessible header for screen readers */}
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation Menu</SheetTitle>
          <SheetDescription>
            Access your chats, settings, and account options
          </SheetDescription>
        </SheetHeader>

        {/* Fixed User Profile Header */}
        <div className="flex-shrink-0 bg-background">
          <div className="flex items-center gap-4 p-6 pb-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-accent text-accent-foreground text-xl font-bold">
                {formattedName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">
                {formattedName}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {formattedCompany}
              </p>
            </div>
          </div>
          <Separator />
        </div>

        {/* Scrollable Content Area
            - flex-1: fills remaining space
            - min-h-0: allows flex item to shrink below content size (required for scroll)
        */}
        <div className="flex-1 min-h-0">
          <ScrollArea className="h-full">
            <div className="flex flex-col">
              {/* Main Navigation */}
              <nav className="flex flex-col" aria-label="Main navigation">
                {/* New Chat */}
                <Button
                  variant="ghost"
                  onClick={handleNewChat}
                  className="h-14 justify-start gap-4 px-6 rounded-none bg-background hover:bg-accent"
                  aria-label="Start new chat"
                >
                  <SquarePen className="h-5 w-5 shrink-0" />
                  <span className="text-base font-medium">New Chat</span>
                </Button>

                <Separator />

                {/* Dashboard */}
                <Button
                  variant="ghost"
                  onClick={handleDashboard}
                  className={cn(
                    'h-14 justify-start gap-4 px-6 rounded-none',
                    isDashboard
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-background hover:bg-accent',
                  )}
                  aria-label="View system dashboard"
                  aria-current={isDashboard ? 'page' : undefined}
                >
                  <BarChart3 className="h-5 w-5 shrink-0" />
                  <span className="text-base font-medium">Dashboard</span>
                </Button>

                <Separator />

                {/* Management */}
                <Button
                  variant="ghost"
                  onClick={handleManagement}
                  className={cn(
                    'h-14 justify-start gap-4 px-6 rounded-none',
                    isManagement
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-background hover:bg-accent',
                  )}
                  aria-label="Manage clients"
                  aria-current={isManagement ? 'page' : undefined}
                >
                  <Users className="h-5 w-5 shrink-0" />
                  <span className="text-base font-medium">Management</span>
                </Button>

                <Separator />

                {/* Recent Conversations */}
                <Collapsible open={showRecent} onOpenChange={setShowRecent}>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-14 justify-start gap-4 px-6 rounded-none bg-background hover:bg-accent w-full"
                      aria-expanded={showRecent}
                    >
                      <Clock className="h-5 w-5 shrink-0" />
                      <span className="text-base font-medium">Recent</span>
                      {recentConversations.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          ({recentConversations.length})
                        </span>
                      )}
                      <ChevronRight
                        className={cn(
                          'h-5 w-5 ml-auto shrink-0 text-muted-foreground transition-transform duration-200',
                          showRecent && 'rotate-90',
                        )}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="bg-muted py-2">
                      {recentConversations.length === 0 ? (
                        <p className="px-6 py-3 text-sm text-muted-foreground">
                          No recent conversations
                        </p>
                      ) : (
                        <ul role="list" aria-label="Recent conversations">
                          {recentConversations.map((conversation) => (
                            <li key={conversation.chatId}>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleConversationSelect(conversation.chatId)
                                }
                                className={cn(
                                  'w-full h-12 justify-start gap-3 px-6 rounded-none text-left',
                                  currentChatId === conversation.chatId
                                    ? 'bg-accent text-accent-foreground'
                                    : 'bg-muted hover:bg-accent',
                                )}
                                aria-current={
                                  currentChatId === conversation.chatId
                                    ? 'page'
                                    : undefined
                                }
                              >
                                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <span className="truncate text-sm">
                                  {conversation.title}
                                </span>
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                {/* Settings */}
                <Collapsible open={showSettings} onOpenChange={setShowSettings}>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-14 justify-start gap-4 px-6 rounded-none bg-background hover:bg-accent w-full"
                      aria-expanded={showSettings}
                    >
                      <Settings className="h-5 w-5 shrink-0" />
                      <span className="text-base font-medium">Settings</span>
                      <ChevronRight
                        className={cn(
                          'h-5 w-5 ml-auto shrink-0 text-muted-foreground transition-transform duration-200',
                          showSettings && 'rotate-90',
                        )}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="bg-muted px-6 py-4 space-y-6">
                      <ColorModeSelector />
                      <Separator />
                      <DemoModeToggle />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />
              </nav>

              {/* Footer Actions */}
              <div className="bg-background mt-auto">
                <Separator />

                {/* Documentation Link */}
                <Button
                  variant="ghost"
                  asChild
                  className="h-14 justify-start gap-4 px-6 rounded-none bg-background hover:bg-accent w-full"
                >
                  <a
                    href={CONFIG.DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Documentation (opens in new tab)"
                  >
                    <BookOpen className="h-5 w-5 shrink-0" />
                    <span className="text-base font-medium">Documentation</span>
                    <ExternalLink
                      className="h-4 w-4 ml-auto shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </a>
                </Button>

                <Separator />

                {/* Log Out */}
                <Button
                  variant="ghost"
                  onClick={handleSignOut}
                  className="h-14 justify-start gap-4 px-6 rounded-none bg-background hover:bg-accent w-full"
                >
                  <LogOut className="h-5 w-5 shrink-0" />
                  <span className="text-base font-medium">Log Out</span>
                </Button>
              </div>
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};
